import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { saveBankDetails } from "../../../services/supplierService";
import moment from "moment";

const BankDetailsForm = ({ register, errors, countryOptions, handleClear, loading, handleSubmit, handleSave }) => (
  <form onSubmit={handleSubmit(handleSave)}>
    <div className="row">
      <div className="col-lg-4">
        <label htmlFor="bank-name" className="form-label">Bank Name</label>
        <input id="bank-name" type="text" className="form-control mb-1" {...register("bankName", { required: "Bank Name is required" })} />
        <small className="text-danger">{errors?.bankName && errors?.bankName?.message}</small>
      </div>
      <div className="col-lg-4">
        <label htmlFor="country" className="form-label">Country</label>
        <select id="country" className="form-select mb-1" {...register("country", { required: "Country is required" })}>
          <option value="">...</option>
          {countryOptions?.map((country) => (
            <option key={country.value} value={country.value}>{country.label}</option>
          ))}
        </select>
        <small className="text-danger mb-2">{errors?.country && errors?.country?.message}</small>
      </div>
      <div className="col-lg-4">
        <label htmlFor="ifsc" className="form-label">Code</label>
        <input id="ifsc" type="text" className="form-control mb-1" {...register("ifsc")} />
        <small className="text-danger mb-2">{errors?.ifsc && errors?.ifsc?.message}</small>
      </div>
      <div className="col-lg-4">
        <label htmlFor="accountNo" className="form-label">Account No.</label>
        <input id="accountNo" type="text" className="form-control mb-1" {...register("accountNo", { required: "Account No. is required" })} />
        <small className="text-danger mb-2">{errors?.accountNo && errors?.accountNo?.message}</small>
      </div>
      <div className="col-lg-4">
        <label htmlFor="accountHolder" className="form-label">Account Holder Name</label>
        <input id="accountHolder" type="text" className="form-control mb-1" {...register("accountHolder", { required: "Account Holder Name is required" })} />
        <small className="text-danger mb-2">{errors?.accountHolder && errors?.accountHolder?.message}</small>
      </div>
    </div>
    <div className="row">
      <div className="col-12 text-center mt-4">
        <button type="button" className="btn btn-label-secondary rounded-pill btn-reset waves-effect me-2" onClick={handleClear} aria-label="Clear">
          Clear
        </button>
        <button type="submit" className="btn btn-primary rounded-pill me-sm-3 me-1 waves-effect waves-light" aria-label="Save" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  </form>
);

const BankDetailsModal = ({ show, onHide, type = "", supplierId, supplier, masterData, flag, setFlag }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);

  const handleClear = () => {
    reset();
    if (onHide) onHide();
  };

  const handleSave = async (data) => {
    try {
      setLoading(true);
      
      const bankData = {
        bank_name: data.bankName,
        country: data.country,
        ifsc_code: data.ifsc,
        account_number: data.accountNo,
        account_holder_name: data.accountHolder,
        aggregator_id: supplierId,
      };

      await saveBankDetails(bankData);
      setFlag(!flag);
      if (onHide) onHide();
      reset();
    } catch (error) {
      console.error("Error saving bank details:", error);
    } finally {
      setLoading(false);
    }
  };

  const countryOptions = masterData?.countries?.map((c) => ({
    label: c.name,
    value: c.id,
    code: c.countryCode,
  }));
  
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <div className="modal-simple modal-edit-user">
        <div className="modal-content p-3 p-md-3">
          <Modal.Body className="modal-body p-2">
            <button type="button" className="btn-close" onClick={onHide} aria-label="Close" />
            <div className="text-left mb-4">
              <h3 className="mb-2">Bank Details</h3>
            </div>
            {type === "Configuration" ? (
              <div className="col-lg-12">
                <BankDetailsForm 
                  register={register}
                  errors={errors}
                  countryOptions={countryOptions}
                  handleClear={handleClear}
                  loading={loading}
                  handleSubmit={handleSubmit}
                  handleSave={handleSave}
                />
              </div>
            ) : (
              <div className="row">
                <p className="mb-2">Previous Bank Accounts Informations</p>
                <div className="card-datatable table-responsive mb-5">
                  <table className="table border" id="orders">
                    <thead>
                      <tr>
                        <th>Bank</th>
                        <th>Account</th>
                        <th>Country</th>
                        <th>Status</th>
                        <th>Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplier?.BankAccounts?.length > 0 ? supplier?.BankAccounts?.map((acc, idx) => (
                        <tr key={idx}>
                          <td>
                            {acc?.bank_name} <br />
                            {acc?.ifsc_code && <small className="text-muted">Code: {acc?.ifsc_code}</small>}
                          </td>
                          <td>
                            {acc?.account_holder_name} <br />
                            <small className="text-muted">{acc?.account_number}</small>
                          </td>
                          <td>{acc?.Country?.name}</td>
                          <td>
                            <span className={`badge px-2 bg-label-${acc?.status ? "success" : "dark"}`}>{acc?.status ? "Active" : "Inactive"}</span>
                          </td>
                          <td>
                            {acc?.created_by?.name}  <br />
                            <small className="text-muted">{moment.utc(acc?.createdAt).format("DD MMM YY, hh:mm A")}</small>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-3">No records found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="mb-0">Add Bank Account</p>
                <p className="small text-muted">Note: Adding new bank account will automatically mark previously added information as inactive</p>
                <div className="col-lg-12">
                  <BankDetailsForm 
                    register={register}
                    errors={errors}
                    countryOptions={countryOptions}
                    handleClear={handleClear}
                    loading={loading}
                    handleSubmit={handleSubmit}
                    handleSave={handleSave}
                  />
                </div>
              </div>
            )}
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

export default BankDetailsModal;
